"use client";

import type {
	Breakpoint,
	CSSObject,
	SxProps,
	Theme,
} from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";
import { Box, GlobalStyles, Stack } from "@mui/material";
import { useBoolean } from "@onair/minimal-ui/hooks/use-boolean";

import Header from "../../common/header";
import { NavMobile } from "./nav-mobile";
import { MenuButton } from "./menu-button";
import { NavVertical } from "./nav-vertical";
import { navData as dashboardNavData } from "./config-nav";

// ----------------------------------------------------------------------

export interface MainLayoutProps {
	sx?: SxProps<Theme>;
	children: React.ReactNode;
	cssVars?: CSSObject;
	pathName?: string;
}

const MainLayout = ({ sx, children, cssVars, pathName }: MainLayoutProps) => {
	const theme = useTheme();

	const mobileNavOpen = useBoolean();

	const layoutQuery: Breakpoint = "lg";

	const isNavMini = false;

	return (
		<>
			<GlobalStyles
				styles={{
					body: {
						"--layout-nav-zIndex": 1101,
						"--layout-nav-mobile-width": "320px",
						"--layout-header-blur": "8px",
						"--layout-header-zIndex": 1100,
						"--layout-header-mobile-height": "64px",
						"--layout-header-desktop-height": "72px",
						"--layout-transition-easing": "linear",
						"--layout-transition-duration": "120ms",
						"--layout-nav-mini-width": "88px",
						"--layout-nav-vertical-width": "300px",
						"--layout-nav-horizontal-height": "64px",
						"--layout-dashboard-content-pt": theme.spacing(1),
						"--layout-dashboard-content-pb": theme.spacing(8),
						"--layout-dashboard-content-px": theme.spacing(5),
						...cssVars,
					},
				}}
			/>
			<NavMobile
				data={dashboardNavData}
				open={mobileNavOpen.value}
				onClose={mobileNavOpen.onFalse}
			/>

			<Box
				id="root__layout"
				className="layout__root"
				sx={{
					"& .layout__has__sidebar": {
						[theme.breakpoints.up(layoutQuery)]: {
							transition: theme.transitions.create(["padding-left"], {
								easing: "var(--layout-transition-easing)",
								duration: "var(--layout-transition-duration)",
							}),
							pl: isNavMini
								? "var(--layout-nav-mini-width)"
								: "var(--layout-nav-vertical-width)",
						},
					},
					...sx,
				}}
			>
				<Header
					slots={{
						button: (
							<MenuButton
								data-slot="menu-button"
								onClick={mobileNavOpen.onTrue}
								sx={{
									mr: 1,
									ml: -1,
									[theme.breakpoints.up(layoutQuery)]: {
										display: "none",
									},
								}}
							/>
						),
					}}
				/>
				<Stack flexDirection="row" flex="1 1 auto">
					<NavVertical data={dashboardNavData} layoutQuery={layoutQuery} />
					<Box component="main" width={1} overflow="auto">
						<Stack flex={1}>{children}</Stack>
					</Box>
				</Stack>
			</Box>
		</>
	);
};

export default MainLayout;
